<template>
  <div class="dept-item-wrapper">
    <div
      class="dept-item"
      :class="{ hasChild, expanded: isExpanded, selected: selectedDept === dept.deptId, leaf: isLeafDept }"
      @click="emit('select-dept', dept.deptId)"
      @dblclick="handleDoubleClick"
    >
      <i
        v-if="hasChild"
        class="icon icon-sm dept-toggle-icon"
        :class="isExpanded ? 'icon-chevron-down' : 'icon-chevron-right'"
        @click.stop="emit('toggle-dept', dept.deptId)"
      ></i>
      <button type="button" class="dept-name-btn" @click="emit('select-dept', dept.deptId)">
        <span class="dept-name">
          {{ isLeafDept ? '- ' : '' }}{{ dept.deptNm }}
          <span v-if="isDeptUnused" class="dept-unused-label">[미사용]</span>
        </span>
      </button>
    </div>

    <div class="sub-departments" v-if="hasChild && isExpanded">
      <DepartmentTreeItem
        v-for="child in children"
        :key="getFullDeptId(child) || child.deptId"
        :dept="child"
        :dept-list="deptList"
        :selected-dept="selectedDept"
        :expanded-depts="expandedDepts"
        @select-dept="$emit('select-dept', $event)"
        @toggle-dept="$emit('toggle-dept', $event)"
        @double-click-dept="(dept) => emit('double-click-dept', dept)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DepartmentTreeItem from './DepartmentTreeItem.vue';

const props = defineProps({
  dept: Object,
  deptList: Array,
  selectedDept: [String, Number],
  expandedDepts: Array,
});

const emit = defineEmits(['select-dept', 'toggle-dept', 'double-click-dept']);

// fullDeptId(전체 경로) 필드를 방어적으로 읽는다. (예: '/D000001/D000198/D000206')
const getFullDeptId = d => String(d?.fullDeptId ?? d?.full_dept_id ?? d?.fullDeptPath ?? '');

// upDeptId가 같아도 deptId가 여러 브랜치에 중복될 수 있으므로,
// fullDeptId 경로가 부모의 fullDeptId로 시작하는 경우에만 자식으로 붙인다.
const children = computed(() => {
  const parentFull = getFullDeptId(props.dept);
  return props.deptList.filter(d => {
    if (d.upDeptId !== props.dept.deptId) return false;
    const childFull = getFullDeptId(d);
    // 부모/자식 모두 fullDeptId가 있을 때만 경로 접두어 비교로 한 번 더 걸러낸다.
    if (parentFull && childFull) {
      return childFull.startsWith(`${parentFull}/`);
    }
    return true;
  });
});
const isLeafDept = computed(() => String(props.dept?.isLeaf ?? '') === 'Y');
const isDeptUnused = computed(() => String(props.dept?.beingYn ?? '') === 'N');
const hasChild = computed(() => {
  if (props.dept?.isLeaf != null) {
    return String(props.dept.isLeaf) === 'N';
  }
  return children.value.length > 0;
});
const isExpanded = computed(() => props.expandedDepts.includes(props.dept.deptId));
const handleDoubleClick = () => {
  // 더블클릭 시 부모로 이벤트 전달 (필요시 부서 정보 같이 전달)
  emit('double-click-dept', props.dept);
}
</script>

<style scoped>
.dept-item-wrapper {
  margin-bottom: 4px;
}
.dept-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.dept-item.selected {
  background-color: rgba(255, 107, 53, 0.15);
}
.dept-item:hover {
  background-color: #f0f0f0;
}
.dept-item.hasChild {
  font-weight: 500;
}
.dept-toggle-icon {
  margin-right: 8px;
  color: #666;
  cursor: pointer;
}
.dept-name-btn {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
}
.dept-unused-label {
  margin-left: 4px;
  color: #888;
  font-weight: 400;
}
.sub-departments {
  margin-left: 24px;
  margin-top: 4px;
}
</style>
